import { useId } from 'react';

/**
 * ClinicStamp — the round seal on a practitioner's badge (Antonin, 13 Oct: "this should remind a clinic access badge,
 * they can even have a subtle stamp of the clinic on them with a blend effect (subtle)").
 *
 * The practice name runs around the ring, "SCRIPS · CARE TEAM" along the bottom, the Scrips mark in the middle. Drawn
 * in the brand blue and meant to sit at low opacity with `mix-blend-mode: multiply` over the badge's tinted band,
 * rotated like an inked stamp, so it reads as texture, not content (it is `aria-hidden`). The capitals are the seal's
 * engraving, an illustration, not UI text.
 */
export function ClinicStamp({ practice, size = 132, className, style }: {
  practice: string;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}) {
  const id = useId().replace(/:/g, '');
  const top = practice.trim().toUpperCase() || 'SCRIPS CLINIC';
  return (
    <svg
      aria-hidden
      viewBox="0 0 132 132"
      width={size}
      height={size}
      className={className}
      style={style}
      fill="none"
    >
      <defs>
        <path id={`${id}-top`} d="M 18 66 A 48 48 0 0 1 114 66" />
        <path id={`${id}-bottom`} d="M 22 66 A 44 44 0 0 0 110 66" />
      </defs>
      <circle cx="66" cy="66" r="62" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="66" cy="66" r="57" stroke="currentColor" strokeWidth="1" />
      <circle cx="66" cy="66" r="34" stroke="currentColor" strokeWidth="1" strokeDasharray="2 3" />
      <text fill="currentColor" fontSize="10.5" fontWeight="700" letterSpacing="2.2" fontFamily="Inter, system-ui, sans-serif">
        <textPath href={`#${id}-top`} startOffset="50%" textAnchor="middle">{top}</textPath>
      </text>
      <text fill="currentColor" fontSize="9" fontWeight="600" letterSpacing="2.4" fontFamily="Inter, system-ui, sans-serif">
        <textPath href={`#${id}-bottom`} startOffset="50%" textAnchor="middle" dominantBaseline="hanging">SCRIPS · CARE TEAM</textPath>
      </text>
      {/* The Scrips mark (public/favicon.svg), centred and scaled into the inner ring. */}
      <g transform="translate(54.2 44.3) scale(0.33)" fill="currentColor">
        <path d="M35.7964 41.3357C40.0077 41.3357 43.4327 37.9094 43.4327 33.6994C43.4327 29.4882 40.0077 26.0632 35.7964 26.0632C31.5864 26.0632 28.1602 29.4882 28.1602 33.6994C28.1602 37.9094 31.5864 41.3357 35.7964 41.3357Z" />
        <path d="M42.5041 48.249H29.0879V100.114H42.5041V48.249Z" />
        <path d="M58.2374 63.0875V70.9238H58.2199V76.7175V89.815V90.9013C58.1937 91.9975 58.0899 93.0713 57.9124 94.1238C57.5649 96.1725 56.9437 98.1275 56.0812 99.9463C54.1137 104.1 50.9062 107.544 46.9387 109.818C45.5249 110.626 44.0187 111.291 42.4337 111.78C40.3424 112.425 38.1212 112.773 35.8174 112.773C33.5137 112.773 31.2924 112.425 29.2012 111.779V112.426V125.556C31.3462 125.956 33.5562 126.175 35.8174 126.175H35.8187C38.0799 126.175 40.2874 125.956 42.4337 125.556C48.3324 124.454 53.7212 121.909 58.2199 118.296C58.2237 118.293 58.2274 118.29 58.2324 118.286C58.4974 118.071 58.7599 117.854 59.0199 117.634C62.5199 114.655 65.4349 111.016 67.5812 106.909C68.2287 105.666 68.8074 104.383 69.3087 103.061C70.4112 100.159 71.1499 97.08 71.4624 93.875C71.5749 92.7175 71.6362 91.5463 71.6362 90.3588V90.3575V90.355V88.4501V76.7188V70.9113V63.0875H58.2374Z" />
        <path d="M13.3988 63.0874V55.2524H13.4163V49.4587V36.3612V35.2749C13.4425 34.1799 13.5463 33.1049 13.7238 32.0524C14.07 30.0037 14.6925 28.0487 15.555 26.2287C17.5225 22.0774 20.73 18.6312 24.6975 16.3587C26.1112 15.5499 27.6175 14.8849 29.2037 14.3962C31.2937 13.7512 33.5163 13.4036 35.8188 13.4036C38.1225 13.4036 40.3437 13.7512 42.435 14.3974V13.7512V0.621155C40.29 0.219902 38.0812 -9.91821e-05 35.8188 -9.91821e-05C33.5575 -9.91821e-05 31.3487 0.219902 29.2037 0.619904C23.3037 1.7224 17.9163 4.2674 13.4163 7.8799C13.4125 7.88365 13.4087 7.88615 13.4037 7.8899C13.1387 8.1049 12.8762 8.32115 12.6162 8.5424C9.1175 11.5211 6.20125 15.1599 4.055 19.2662C3.4075 20.5087 2.82875 21.7937 2.3275 23.1149C1.225 26.0174 0.48625 29.0962 0.17375 32.3012C0.0612499 33.4587 0 34.6299 0 35.8174V35.8187V35.8212V37.7262V49.4574V55.2649V63.0874L13.3988 63.0874Z" />
      </g>
    </svg>
  );
}

export default ClinicStamp;
